import type { DeleteDocumentFilesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface DeleteDocumentFilesTRPCInput {
  documentFileIds: string[];
}

/**
 * Maps the TRPC mutation response (`{ Ids: string[] }`) to match the legacy
 * GraphQL shape (`{ delete_file: { affected_rows }, delete_document_file: { affected_rows } }`)
 * so consumers can be migrated 1:1.
 *
 * `delete_document_file.affected_rows` = `Ids.length` (deleted document_file rows).
 * `delete_file.affected_rows` is set to 0 — the cascaded `file` row count is
 * not surfaced by the data-layer today. Html/Link versions have no `file`
 * row, so reusing `Ids.length` for both fields (the prior implementation)
 * would have inflated the count. Consumers currently only branch on
 * success/error, not on the count, so 0 is safe; if a follow-up needs the
 * exact file count it should be added to the data-layer response.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): DeleteDocumentFilesMutation => {
  return {
    delete_file: {
      __typename: 'file_mutation_response',
      affected_rows: 0,
    },
    delete_document_file: {
      __typename: 'document_file_mutation_response',
      affected_rows: trpcData.Ids.length,
    },
  };
};

export const useDeleteDocumentFilesTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.documentFile.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.documentFile.documentFilesByDocumentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.documentFile.latestDocumentFile.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.documentFile.documentFileById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.documentFile.publicDocumentFiles.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.documentFile.latestPublicDocumentFileByDocumentId.queryKey(),
        });
      },
    }),
  });

  return {
    deleteDocumentFiles: async (
      variables: DeleteDocumentFilesTRPCInput
    ): Promise<DeleteDocumentFilesMutation> => {
      const result = await mutation.mutateAsync({
        Ids: variables.documentFileIds,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
